import * as _ from 'lodash';
import { format } from 'date-fns';
import type { VoidFunctionComponent } from 'react';
import DrawerSection from './Section';
import DetailsPanel, { DetailsPanelList, DetailsPanelLink } from '../../../generic/DetailsPanel';
import Badge, { BadgeColors, BadgeShapes } from '../../../generic/Badge/Badge';
import CVEStatusText from './CVEStatusText';
import type { CVEStatus } from '../../../../../typings/enums';

//  Takes the CWE (which generally looks like `CWE-1234`) and returns the URL for the Mitre CWE URL for that CWE.
const getCWEURL = (cwe: string) => {
  if (!cwe.startsWith('CWE-')) {
    return `https://cwe.mitre.org/data/definitions/${cwe}.html`;
  }
  return `https://cwe.mitre.org/data/definitions/${cwe.slice(4)}.html`;
};

//  Returns the NIST URL for a given CVE.
const getCVEURL = (cve: string) => `https://nvd.nist.gov/vuln/detail/${cve}`;

//  Takes a Date object and returns a formatted date string.
const formatPublishedDate = (date: Date) => format(new Date(date), 'MMM d, yyyy');

export type VulnDetails = {
  cve: string | null;
  cwes: string[] | null;
  affectedVersionRanges: string[];
  patchedVersionRanges: string[];
  published: Date | null;
  cveStatus: string;
  epss: string | null;
};

type Props = {
  isLoading?: boolean;
  selectedVulnDetails: VulnDetails | null;
};

/**
 * VulnerabilityDrawerDetails is a collapsable panel which displays
 * various metadata about the vuln.
 */
const VulnerabilityDrawerDetails: VoidFunctionComponent<Props> = ({
  isLoading,
  selectedVulnDetails,
}) => {
  //  Note that we do a null check to ensure that TS understands that the values exist below.
  const rows = !selectedVulnDetails ? [] : _.compact([
    !!selectedVulnDetails.cve && {
      label: 'CVE',
      content: (
        <DetailsPanelLink to={getCVEURL(selectedVulnDetails.cve)}>
          {selectedVulnDetails.cve}
        </DetailsPanelLink>
      ),
    },
    (selectedVulnDetails.cwes?.length ?? 0) > 0 && {
      label: 'CWEs',
      content: selectedVulnDetails.cwes?.map(cwe => (
        <DetailsPanelLink key={cwe} to={getCWEURL(cwe)}>
          {cwe}
        </DetailsPanelLink>),
      ),
    },
    !!selectedVulnDetails.epss && {
      label: 'EPSS Score',
      content: (
        <span className="font-family-monospace">
          {selectedVulnDetails.epss}
        </span>
      ),
    },
    selectedVulnDetails.affectedVersionRanges.length > 0 && {
      label: 'Affected versions',
      content: selectedVulnDetails.affectedVersionRanges.map(version => (
        <Badge
          key={version}
          color={BadgeColors.LightRed}
          shape={BadgeShapes.Circle}
        >
          {version}
        </Badge>
      )),
    },
    selectedVulnDetails.patchedVersionRanges.length > 0 && {
      label: 'Patched versions',
      content: selectedVulnDetails.patchedVersionRanges.map(version => (
        <Badge
          key={version}
          color={BadgeColors.LightGreen}
          shape={BadgeShapes.Circle}
        >
          {version}
        </Badge>
      )),
    },
    selectedVulnDetails.published && {
      label: 'Publication date',
      content: (
        <span className="font-family-monospace">
          {formatPublishedDate(selectedVulnDetails.published)}
        </span>
      ),
    },
    selectedVulnDetails.cveStatus && {
      label: 'Review status',
      content: (
        <span className="font-family-monospace">
          <CVEStatusText
            status={selectedVulnDetails.cveStatus as CVEStatus}
            tooltipPlacement="left"
          />
        </span>
      ),
    },
  ]);

  //  Don't render anything if there is no details to display.
  return rows.length > 0 ? (
    <DrawerSection>
      <DetailsPanel
        id="package-vulns-drawer-details"
        target="Vulnerability details"
        isLoading={!selectedVulnDetails || isLoading}
      >
        <DetailsPanelList rows={rows} />
      </DetailsPanel>
    </DrawerSection>
  ) : null;
};

export default VulnerabilityDrawerDetails;
